import * as React from "react"
import { MdSend } from "react-icons/md"
import * as Yup from "yup"
import {
  StyledModalActions,
  Button,
  TextAreaFieldBlock,
} from "gatsby-interface"

import { envVars as text, ui as uiText } from "@modules/locales/default.js"
import { interpolateMessage } from "@modules/locales"
import { StyledForm } from "@modules/ui/components/Primitives"

type FormValues = {
  envVarsList: string
}

const validationSchema = Yup.object<FormValues>().shape({
  envVarsList: Yup.string()
    .required(text.messages.bulkAddValidateRequired)
    .test(
      "isValidEnvVarsString",
      text.messages.bulkAddValidateFormatShort,
      function(value) {
        const { invalidEnvVars } = formatBulkEnvVars(value)

        if (invalidEnvVars && invalidEnvVars.length) {
          return this.createError({
            message: interpolateMessage<"invalidEnvVars">(
              text.messages.bulkAddValidateFormat,
              {
                invalidEnvVars: invalidEnvVars.join(", "),
              }
            ),
          })
        }

        return true
      }
    ),
})

export type EnvVarsBulkFormProps = {
  initialEnvVars: { key: string; value: string }[]
  onSubmit: (envVars: { [k: string]: string }) => void
  onCancel: () => void
}

export function EnvVarsBulkForm({
  initialEnvVars,
  onSubmit,
  onCancel,
}: EnvVarsBulkFormProps) {
  const [envVarsString, setEnvVarsString] = React.useState<string>(() => {
    return initialEnvVars.map(({ key, value }) => `${key}=${value}`).join("\n")
  })
  const [error, setError] = React.useState<string>()

  const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = e => {
    setEnvVarsString(e.target.value)
  }

  const handleBlur: React.FocusEventHandler<HTMLTextAreaElement> = e => {
    try {
      validationSchema.validateSync(
        { envVarsList: envVarsString },
        { abortEarly: false }
      )
    } catch (validationError) {
      setError((validationError as Yup.ValidationError).message)
      return
    }
    setError(undefined)
  }

  return (
    <StyledForm
      data-cy="bulk-add-env-vars-form"
      onSubmit={e => {
        e.preventDefault()
        e.stopPropagation()

        setError(undefined)

        try {
          validationSchema.validateSync(
            { envVarsList: envVarsString },
            { abortEarly: false }
          )
        } catch (validationError) {
          setError((validationError as Yup.ValidationError).message)
          return
        }

        const { validEnvVarsLookup } = formatBulkEnvVars(envVarsString)
        onSubmit(validEnvVarsLookup)
      }}
    >
      <TextAreaFieldBlock
        id="envVarsList"
        name="envVarsList"
        label={text.labels.bulkAdd}
        hint={text.messages.bulkAddFormat}
        placeholder={text.labels.bulkAddPlaceholder}
        className="fs-block"
        data-cy="bulk-add-env-vars-textarea"
        value={envVarsString}
        onChange={handleChange}
        onBlur={handleBlur}
        error={error}
        rows={10}
      />
      <StyledModalActions>
        <Button
          variant="SECONDARY"
          onClick={onCancel}
          size={`L`}
          type={`reset`}
        >
          {uiText.actions.cancel}
        </Button>
        <Button
          variant="PRIMARY"
          type="submit"
          data-cy="submit-bulk-env-vars-button"
          rightIcon={<MdSend />}
        >
          {text.actions.bulkSubmit}
        </Button>
      </StyledModalActions>
    </StyledForm>
  )
}

export function formatBulkEnvVars(str = ``) {
  if (!str.length) {
    return {
      validEnvVarsLookup: {},
      invalidEnvVars: [],
    }
  }

  const invalidEnvVars: string[] = []
  const validatedEnvVars: { [key: string]: string } = {}

  const definitions = str
    .trim()
    .split(/\n/)
    .filter(Boolean)

  for (const envVardDef of definitions) {
    const firstEqualsCharIndex = envVardDef.indexOf(`=`)

    if (firstEqualsCharIndex <= 0) {
      invalidEnvVars.push(envVardDef)
      continue
    }

    const key = envVardDef.slice(0, firstEqualsCharIndex).trim()
    const value = envVardDef.slice(firstEqualsCharIndex + 1).trim()

    if (key && value) {
      validatedEnvVars[key] = stripQuotes(value)
    } else if (!value) {
      invalidEnvVars.push(key)
    }
  }

  return {
    validEnvVarsLookup: validatedEnvVars,
    invalidEnvVars,
  }
}

export const stripQuotes = (str: string) => {
  const quoteChars = [`"`, `'`]
  const first = str.slice(0, 1)
  const last = str.slice(-1)
  if (first === last && quoteChars.find(char => char === first)) {
    return str.slice(1).slice(0, -1)
  }
  return str
}
